// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getProperties, selectAppliedFilters, applyFilters, resetState } from '../../feature';

// Components
import { StyledFilterRow, StyledInput, StyledNumberInput, StyledSelect } from './PropertiesFiltersContainerStyles';

// Helpers
import { GetPropertiesDTO } from '../../dtos';
import { getPropertyTypeOptions, getStepTypeOptions, PropertyFiltersFields } from '../../helpers';

type IAdminFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const PropertiesFiltersContainer = ({ children, className }: IAdminFiltersContainerProps) => {
  const { t } = useTranslation(['order', 'common']);
  const dispatch = useAppDispatch();
  const filters = useAppState(selectAppliedFilters);

  const handleDebounceSearch = useCallback(
    debounce(async (searchParams) => {
      const dto = new GetPropertiesDTO(searchParams);
      dispatch(getProperties(dto));
    }, 500),
    [dispatch],
  );

  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const newQuery = { ...filters, [name]: value || undefined };

      dispatch(applyFilters(newQuery));
      handleDebounceSearch(newQuery);
    },
    [filters],
  );

  const handleSelect = useCallback(
    (name: string) => (value: unknown) => {
      const newQuery = {
        ...filters,
        [name]: value,
      };
      dispatch(applyFilters(newQuery));
      handleDebounceSearch(newQuery);
    },
    [filters],
  );

  useEffect(() => {
    return () => {
      dispatch(resetState());
      handleDebounceSearch.cancel();
    };
  }, []);

  const handleChangeOrderSum = useCallback(
    (value: number | null | string) => {
      const newQuery = {
        ...filters,
        [PropertyFiltersFields.ORDERS_SUM]: value?.toString(),
      };
      dispatch(applyFilters(newQuery));
      handleDebounceSearch(newQuery);
    },
    [filters],
  );

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th />
        <th>
          <StyledInput
            onChange={handleSearch}
            name={PropertyFiltersFields.ADDRESS}
            placeholder={t('common_search', { ns: 'common' })}
            allowClear
            size="middle"
          />
        </th>
        <th />
        <th />
        <th />
        <th>
          <StyledSelect
            onChange={handleSelect(PropertyFiltersFields.TYPE)}
            options={getPropertyTypeOptions(t)}
            suffixIcon={null}
            size="middle"
            placeholder={t('order_type')}
            isMultiselect
            allowClear
            maxTagCount={1}
            optionLabelProp="label"
          />
        </th>
        <th>
          <StyledInput
            onChange={handleSearch}
            placeholder={t('common_search', { ns: 'common' })}
            name={PropertyFiltersFields.EMAIL}
            size="middle"
            allowClear
          />
        </th>
        <th>
          <StyledInput
            onChange={handleSearch}
            placeholder={t('common_search', { ns: 'common' })}
            name={PropertyFiltersFields.OWNERS}
            size="middle"
            allowClear
          />
        </th>
        <th>
          <StyledInput
            onChange={handleSearch}
            name={PropertyFiltersFields.PHONE_NUMBER}
            placeholder={t('common_search', { ns: 'common' })}
            allowClear
            size="middle"
          />
        </th>
        <th>
          <StyledNumberInput
            onChange={handleChangeOrderSum}
            name={PropertyFiltersFields.ORDERS_SUM}
            placeholder={t('common_search_more_than', { ns: 'common' })}
            controls={false}
            size="middle"
          />
        </th>
        <th />
        <th>
          <StyledSelect
            onChange={handleSelect(PropertyFiltersFields.PROGRESS)}
            options={getStepTypeOptions(t)}
            suffixIcon={null}
            size="middle"
            placeholder={t('order_progress')}
            isMultiselect
            allowClear
            maxTagCount={1}
            optionLabelProp="label"
          />
        </th>
      </StyledFilterRow>
    </thead>
  );
};

export default memo(PropertiesFiltersContainer);
